class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.bundleName = `bundle-builder-${this.dataset.bundleId}`;
    this.forms = [];
    this.animationIndexFrame = null;
    this.requestProductAnimationIndexUpdate = this.requestProductAnimationIndexUpdate.bind(this);
    this.handleMobileStickyCardUpdate = this.handleMobileStickyCardUpdate.bind(this);
  }

  connectedCallback() {
    this.forms = Array.from(this.querySelectorAll('product-bundle-card-form'));
    this.forms.forEach((form) => this.initProductForm(form));
    this.updateProductAnimationIndexes();
    this.querySelectorAll('[data-bundle-builder-add-to-cart]').forEach((button) => {
      button.addEventListener('click', (event) => this.addBundleToCart(event, button));
    });
    this.querySelectorAll('[data-bundle-builder-checkout]').forEach((button) => {
      button.addEventListener('click', (event) => this.checkoutBundle(event));
    });
    this.initStickyCard();
    this.build();
    window.addEventListener('resize', this.requestProductAnimationIndexUpdate);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.requestProductAnimationIndexUpdate);
    window.removeEventListener('scroll', this.handleMobileStickyCardUpdate);
    window.removeEventListener('resize', this.handleMobileStickyCardUpdate);

    if (this.animationIndexFrame) {
      window.cancelAnimationFrame(this.animationIndexFrame);
      this.animationIndexFrame = null;
    }
  }

  getGridColumnCount(grid) {
    const columns = window.getComputedStyle(grid).gridTemplateColumns;

    if (!columns || columns === 'none') return 1;

    return Math.max(1, columns.split(' ').filter(Boolean).length);
  }

  updateProductAnimationIndexes() {
    this.querySelectorAll('.bundle-builder-products').forEach((productList) => {
      const columnCount = this.getGridColumnCount(productList);

      productList.querySelectorAll('.product-item').forEach((item, index) => {
        item.style.setProperty('--animation-index', index % columnCount);
      });
    });
  }

  requestProductAnimationIndexUpdate() {
    if (this.animationIndexFrame) return;

    this.animationIndexFrame = window.requestAnimationFrame(() => {
      this.animationIndexFrame = null;
      this.updateProductAnimationIndexes();
    });
  }

  initProductForm(form) {
    form.bundleVariants = this.getProductVariants(form);
    form.addEventListener('change', () => this.updateSelectedVariant(form));
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      await this.addToBundle(form);
    });
    this.initProductSwatches(form);
    this.updateSelectedVariant(form);
  }

  getProductVariants(form) {
    const script = form.querySelector('[data-product-variants]');
    if (!script) return [];

    try {
      const variants = JSON.parse(script.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.error('Error parsing bundle product variants:', error);
      return [];
    }
  }

  getBundleContents() {
    try {
      return JSON.parse(localStorage.getItem(this.bundleName)) || [];
    } catch (error) {
      console.error('Error parsing bundle contents:', error);
      return [];
    }
  }

  setBundleContents(contents) {
    localStorage.setItem(this.bundleName, JSON.stringify(contents));
  }

  clearBundleContents() {
    localStorage.removeItem(this.bundleName);
  }

  bundleQuantity() {
    return this.getBundleContents().reduce((sum, item) => sum + Number(item.quantity), 0);
  }

  bundleTotalPrice() {
    return this.getBundleContents().reduce((sum, item) => {
      return sum + Number(item.variant_price) * Number(item.quantity);
    }, 0);
  }

  parseTiers() {
    const source = this.dataset.bundleMode === 'quantity'
      ? this.dataset.bundleQuantityTiers
      : this.dataset.bundleAmountTiers;

    return String(source || '').split(';').map((tier) => {
      const [value, discount] = tier.split(':').map((part) => Number(String(part).trim()));
      return { value, discount };
    }).filter((tier) => Number.isFinite(tier.value) && Number.isFinite(tier.discount));
  }

  getTierDiscount() {
    const currentValue = this.dataset.bundleMode === 'quantity'
      ? this.bundleQuantity()
      : this.bundleTotalPrice() / 100;

    return this.parseTiers().reduce((discount, tier) => {
      return currentValue >= tier.value ? tier.discount : discount;
    }, 0);
  }

  getNextTier() {
    const currentValue = this.dataset.bundleMode === 'quantity'
      ? this.bundleQuantity()
      : this.bundleTotalPrice() / 100;

    return this.parseTiers().find((tier) => currentValue < tier.value);
  }

  escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  getSelectedVariant(form) {
    const input = form.querySelector('[data-bundle-product-variant]');
    const variantId = Number(input?.value);
    return form.bundleVariants.find((variant) => Number(variant.id) === variantId) || null;
  }

  initProductSwatches(form) {
    const productCard = form.closest('.product-card');
    const swatches = productCard?.querySelector('product-color-swatches');
    if (!swatches) return;

    swatches.addEventListener('change', (event) => {
      const input = event.target.closest('input[type="radio"][data-option-position]');
      if (!input || !swatches.contains(input)) return;

      this.selectVariantFromSwatch(form, input);
    });
  }

  selectVariantFromSwatch(form, input) {
    const select = form.querySelector('select[name="id"]');
    if (!select) return;

    const matchingOption = Array.from(select.options).find((option) => {
      return option.getAttribute(`data-option-${input.dataset.optionPosition}`) === input.value && !option.disabled;
    });

    if (!matchingOption || matchingOption.selected) return;

    select.value = matchingOption.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updateSelectedVariant(form) {
    const variant = this.getSelectedVariant(form);
    const button = form.querySelector('button[type="submit"]');
    const article = form.closest('.bundle-product-card');

    if (!variant) {
      if (button) button.disabled = true;
      return;
    }

    form.dataset.selectedVariantAvailable = String(variant.available);
    form.dataset.selectedVariantTitle = variant.title;
    form.dataset.selectedVariantPrice = String(variant.price);
    form.dataset.selectedVariantImage = variant.image || '';

    if (article) {
      this.updateCardPrice(article, variant);
      this.updateCardImage(article, variant);
      this.updateCardLink(article, variant);
      this.updateCardSaleBadge(article, form);
      this.updateCardSwatches(article, form);
    }

    if (button) {
      button.disabled = !variant.available || this.isAtMax();
      const text = button.querySelector('span') || button;
      text.textContent = variant.available
        ? this.dataset.textAddToBundle
        : window.theme?.strings?.product?.soldOut || 'Sold out';
    }
  }

  updateCardPrice(article, variant) {
    article.dispatchEvent(new CustomEvent('product-card:variant-change', {
      bubbles: true,
      detail: {
        variantId: variant.id
      }
    }));
  }

  updateCardImage(article, variant) {
    if (!variant.image) return;

    const image = article.querySelector('.product-card-media img');
    if (image) {
      image.src = variant.image;
      image.srcset = '';
    }
  }

  updateCardLink(article, variant) {
    article.querySelectorAll('a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('variant', variant.id);
      link.href = url.toString();
    });
  }

  updateCardSaleBadge(article, form) {
    const select = form.querySelector('select[name="id"]');
    const selectedOption = select?.options[select.selectedIndex];
    const saleBadge = article.querySelector('[data-product-card-sale-badge]');
    if (!selectedOption || !saleBadge) return;

    const saleBadgeType = saleBadge.dataset.saleBadgeType === 'value' ? 'value' : 'percentage';
    const saleBadgeText = selectedOption.getAttribute(`data-sale-badge-${saleBadgeType}`) || '';
    saleBadge.textContent = saleBadgeText;
    saleBadge.hidden = saleBadgeText === '';
  }

  updateCardSwatches(article, form) {
    const select = form.querySelector('select[name="id"]');
    const selectedOption = select?.options[select.selectedIndex];
    if (!selectedOption) return;

    article.querySelectorAll('product-color-swatches input[type="radio"][data-option-position]').forEach((input) => {
      const optionValue = selectedOption.getAttribute(`data-option-${input.dataset.optionPosition}`);

      if (optionValue && input.value === optionValue && !input.checked) {
        input.checked = true;
      }
    });
  }

  isAtMax() {
    const bundleMax = Number(this.dataset.bundleMax);
    return bundleMax > 0 && this.bundleQuantity() >= bundleMax;
  }

  async addToBundle(form) {
    const selectedVariant = this.getSelectedVariant(form);
    if (!selectedVariant || !selectedVariant.available || this.isAtMax()) return;

    const animationPromise = this.animateProductToSummary(form);
    const contents = this.getBundleContents();
    const existing = contents.find((item) => Number(item.variant_id) === Number(selectedVariant.id));

    if (existing) {
      existing.quantity = Number(existing.quantity) + 1;
    } else {
      contents.push({
        product_id: Number(form.dataset.productId),
        product_handle: form.dataset.productHandle,
        product_title: form.dataset.productTitle,
        product_url: form.dataset.productUrl,
        variant_id: Number(selectedVariant.id),
        variant_title: selectedVariant.title,
        variant_price: Number(selectedVariant.price),
        variant_image: selectedVariant.image,
        quantity: 1
      });
    }

    const bundleMax = Number(this.dataset.bundleMax);
    if (bundleMax > 0) {
      let remaining = bundleMax;
      contents.forEach((item) => {
        item.quantity = Math.min(Number(item.quantity), Math.max(remaining, 0));
        remaining -= item.quantity;
      });
    }

    this.setBundleContents(contents.filter((item) => Number(item.quantity) > 0));
    this.flashAddedState(form);
    this.build();
    await animationPromise;
  }

  async animateProductToSummary(form) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const productCard = form.closest('.product-card');
    const sourceImage = productCard?.querySelector('.product-card-media-image-primary, .product-card-media img');
    const sourceButton = form.querySelector('button[type="submit"]');
    const targetCard = this.getVisibleSummaryCard();

    if (!sourceImage || !sourceButton || !targetCard) {
      return;
    }

    const sourceRect = sourceButton.getBoundingClientRect();
    const targetRect = targetCard.getBoundingClientRect();
    const size = 60;
    const startX = sourceRect.left + sourceRect.width / 2 - size / 2;
    const startY = sourceRect.top + sourceRect.height / 2 - size / 2;
    const targetX = targetRect.left + Math.min(targetRect.width / 2, 96) - size / 2;
    const targetY = targetRect.top + Math.min(targetRect.height / 2, 96) - size / 2;
    const image = document.createElement('img');

    image.src = sourceImage.currentSrc || sourceImage.src;
    image.alt = '';
    image.className = 'cart-flyout-image';
    image.style.left = `${startX}px`;
    image.style.top = `${startY}px`;
    document.body.appendChild(image);

    const animation = image.animate(
      [
        {
          opacity: 1,
          transform: 'translate3d(0, 0, 0) scale(1)',
        },
        {
          opacity: 0.95,
          transform: 'translate3d(-30px, -42px, 0) scale(0.92)',
          offset: 0.28,
        },
        {
          opacity: 0.95,
          transform: `translate3d(${(targetX - startX) * 0.58 - 12}px, ${(targetY - startY) * 0.42 - 28}px, 0) scale(0.78)`,
          offset: 0.66,
        },
        {
          opacity: 0,
          transform: `translate3d(${targetX - startX}px, ${targetY - startY}px, 0) scale(0.35)`,
        },
      ],
      {
        duration: 1000,
        easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
      }
    );

    await animation.finished.catch(() => {});
    image.remove();
  }

  getVisibleSummaryCard() {
    if (this.stickyCard?.classList.contains('show')) {
      return this.stickyCard;
    }

    const mainCard = this.querySelector('.bundle-builder-card:not(.bundle-builder-sticky-card)');
    if (!mainCard) return null;

    const rect = mainCard.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < window.innerHeight) {
      return mainCard;
    }

    return this.stickyCard || mainCard;
  }

  flashAddedState(form) {
    const buttonText = form.querySelector('button[type="submit"] span');
    if (!buttonText) return;

    buttonText.textContent = this.dataset.textAddedToBundle;
    window.setTimeout(() => {
      buttonText.textContent = this.dataset.textAddToBundle;
    }, 1600);
  }

  build() {
    this.updateProgress();
    this.updatePromoText();
    this.updateTotals();
    this.updateActions();
    this.updateItemCount();
    this.renderContents();
    this.handleMobileStickyCardUpdate();
  }

  updateProgress() {
    const maxTierValue = Number(this.dataset.maxTierValue);
    const currentValue = this.dataset.bundleMode === 'quantity'
      ? this.bundleQuantity()
      : this.bundleTotalPrice();
    const adjustedMax = this.dataset.bundleMode === 'quantity' ? maxTierValue : maxTierValue * 100;
    const percent = adjustedMax > 0 ? Math.min(100, Math.round((currentValue / adjustedMax) * 100)) : 0;

    this.querySelectorAll('.bundle-builder-progress').forEach((progress) => {
      progress.setAttribute('aria-valuenow', String(percent));
      progress.querySelector('.bundle-builder-progress-bar').style.width = `${percent}%`;
      progress.querySelectorAll('.bundle-builder-progress-tier').forEach((tier) => {
        tier.classList.toggle('active', Number(tier.dataset.tierPercent) <= percent);
      });
    });
  }

  updatePromoText() {
    const nextTier = this.getNextTier();

    this.querySelectorAll('.bundle-builder-promo-text').forEach((element) => {
      let html = this.dataset.textPromoFinalHtml;
      element.classList.toggle('is-complete', !nextTier);

      if (nextTier && this.dataset.bundleMode === 'amount') {
        const remaining = Math.max(0, nextTier.value * 100 - this.bundleTotalPrice());
        html = this.dataset.textPromoAmountHtml
          .replace('{{ amount }}', window.Shopify.formatMoney(remaining))
          .replace('{{ discount }}', `${nextTier.discount}%`);
      } else if (nextTier) {
        const remaining = Math.max(0, nextTier.value - this.bundleQuantity());
        html = this.dataset.textPromoQuantityHtml
          .replace('{{ count }}', remaining)
          .replace('{{ discount }}', `${nextTier.discount}%`);
      }

      element.innerHTML = html;
    });
  }

  updateTotals() {
    const total = this.bundleTotalPrice();
    const discount = this.getTierDiscount();
    const discountedTotal = Math.round(total - total * discount / 100);
    const savings = total - discountedTotal;

    this.querySelectorAll('[data-bundle-builder-subtotal]').forEach((element) => {
      element.innerHTML = discount > 0
        ? `<s>${window.Shopify.formatMoney(total)}</s><span>${window.Shopify.formatMoney(discountedTotal)}</span>`
        : `<span>${window.Shopify.formatMoney(total)}</span>`;
    });

    this.querySelectorAll('[data-bundle-builder-savings]').forEach((element) => {
      element.innerHTML = `<span>${window.Shopify.formatMoney(savings)}</span><span>(${discount}%)</span>`;
    });
  }

  updateActions() {
    const hasItems = this.bundleQuantity() > 0;
    const bundleMin = Number(this.dataset.bundleMin);
    const belowMin = bundleMin > 0 && this.bundleQuantity() < bundleMin;
    const actionDisabled = !hasItems || belowMin;

    this.querySelectorAll('[data-bundle-builder-action]').forEach((button) => {
      button.disabled = actionDisabled;
    });

    this.forms.forEach((form) => this.updateSelectedVariant(form));
  }

  updateItemCount() {
    this.querySelectorAll('[data-bundle-builder-item-count]').forEach((element) => {
      element.textContent = this.bundleQuantity();
    });
  }

  renderContents() {
    const list = this.querySelector('.bundle-builder-contents-list');
    const empty = this.querySelector('.bundle-builder-contents-empty');
    if (!list || !empty) return;

    const contents = this.getBundleContents();
    empty.hidden = contents.length > 0;
    list.hidden = contents.length === 0;

    list.innerHTML = contents.map((item) => this.renderContentItem(item)).join('');
    this.bindContentEvents(list);
  }

  renderContentItem(item) {
    const variantId = Number(item.variant_id);
    const productUrl = this.escapeHTML(item.product_url || '');
    const productTitle = this.escapeHTML(item.product_title || '');
    const variantTitle = this.escapeHTML(item.variant_title || '');
    const image = this.escapeHTML(item.variant_image || '');
    const hideVariantTitle = !variantTitle || variantTitle.includes('Default');

    return `
      <li class="bundle-builder-contents-item">
        <a class="bundle-builder-contents-item-image" href="${productUrl}?variant=${variantId}" tabindex="-1">
          ${image ? `<img src="${image}" alt="" width="160" height="160" loading="lazy">` : ''}
        </a>
        <div class="bundle-builder-contents-item-content">
          <h3 class="bundle-builder-contents-item-title h6">
            <a href="${productUrl}?variant=${variantId}">${productTitle}</a>
          </h3>
          <p class="bundle-builder-contents-item-meta fs-sm">
            <span ${hideVariantTitle ? 'hidden' : ''}>${variantTitle} - </span>
            <span>${window.Shopify.formatMoney(Number(item.variant_price) * Number(item.quantity))}</span>
          </p>
          <button class="btn-icon bundle-builder-remove" type="button" data-bundle-builder-remove="${variantId}" aria-label="${this.dataset.textRemoveFromBundle}">
            <span class="animated-bin-icon" aria-hidden="true">
              <span></span>
              <span></span>
            </span>
          </button>
          <div class="qty-switcher bundle-builder-quantity" role="group" aria-label="${this.dataset.textQuantityLabel}">
            <button class="btn" type="button" name="decrease" data-bundle-builder-decrease="${variantId}" aria-label="${this.dataset.textQuantityDecrease}">
              <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <input class="input" type="number" min="0" value="${Number(item.quantity)}" data-bundle-builder-quantity="${variantId}" aria-label="${this.dataset.textQuantityLabel}">
            <button class="btn" type="button" name="increase" data-bundle-builder-increase="${variantId}" aria-label="${this.dataset.textQuantityIncrease}" ${this.isAtMax() ? 'disabled' : ''}>
              <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      </li>
    `;
  }

  bindContentEvents(list) {
    list.querySelectorAll('[data-bundle-builder-remove]').forEach((button) => {
      button.addEventListener('click', () => this.setVariantQuantity(Number(button.dataset.bundleBuilderRemove), 0));
    });
    list.querySelectorAll('[data-bundle-builder-decrease]').forEach((button) => {
      button.addEventListener('click', () => {
        const item = this.getBundleContents().find((contentItem) => Number(contentItem.variant_id) === Number(button.dataset.bundleBuilderDecrease));
        this.setVariantQuantity(Number(button.dataset.bundleBuilderDecrease), Number(item?.quantity || 0) - 1);
      });
    });
    list.querySelectorAll('[data-bundle-builder-increase]').forEach((button) => {
      button.addEventListener('click', () => {
        const item = this.getBundleContents().find((contentItem) => Number(contentItem.variant_id) === Number(button.dataset.bundleBuilderIncrease));
        this.setVariantQuantity(Number(button.dataset.bundleBuilderIncrease), Number(item?.quantity || 0) + 1);
      });
    });
    list.querySelectorAll('[data-bundle-builder-quantity]').forEach((input) => {
      input.addEventListener('change', () => {
        this.setVariantQuantity(Number(input.dataset.bundleBuilderQuantity), Number(input.value));
      });
    });
  }

  setVariantQuantity(variantId, quantity) {
    const bundleMax = Number(this.dataset.bundleMax);
    let contents = this.getBundleContents();
    const otherQuantity = contents.reduce((sum, item) => {
      return Number(item.variant_id) === variantId ? sum : sum + Number(item.quantity);
    }, 0);
    const nextQuantity = bundleMax > 0
      ? Math.min(Math.max(quantity, 0), Math.max(bundleMax - otherQuantity, 0))
      : Math.max(quantity, 0);

    contents = contents.map((item) => {
      if (Number(item.variant_id) !== variantId) return item;
      return { ...item, quantity: nextQuantity };
    }).filter((item) => Number(item.quantity) > 0);

    this.setBundleContents(contents);
    this.build();
  }

  initStickyCard() {
    this.mainCard = this.querySelector('.bundle-builder-card:not(.bundle-builder-sticky-card)');
    this.stickyCard = this.querySelector('.bundle-builder-sticky-card');
    if (!this.mainCard || !this.stickyCard) return;

    window.addEventListener('scroll', this.handleMobileStickyCardUpdate, { passive: true });
    window.addEventListener('resize', this.handleMobileStickyCardUpdate);
    this.handleMobileStickyCardUpdate();
  }

  handleMobileStickyCardUpdate() {
    if (!this.mainCard || !this.stickyCard) return;

    const mainCardBottom = this.mainCard.getBoundingClientRect().bottom + window.scrollY;
    const sectionBottom = this.getBoundingClientRect().bottom + window.scrollY;
    const shouldShow = window.scrollY > mainCardBottom && window.scrollY < sectionBottom - window.innerHeight;
    this.stickyCard.classList.toggle('show', shouldShow);
  }

  getCartFormData() {
    const formData = new FormData();
    this.getBundleContents().forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.variant_id);
      formData.append(`items[${index}][quantity]`, item.quantity);
    });
    formData.append('sections', 'cart-badge');
    formData.append('sections_url', window.location.pathname);
    return formData;
  }

  async addBundleToCart(event, button) {
    event.preventDefault();
    if (this.bundleQuantity() === 0) return;

    button.classList.add('loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.theme.routes.cart.add}.js`, {
        method: 'POST',
        body: this.getCartFormData()
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Unable to add bundle to cart.');
      }

      this.clearBundleContents();
      this.build();
      await this.refreshCart(data, button);
      window.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
    } finally {
      button.classList.remove('loading');
      button.disabled = false;
      button.removeAttribute('aria-busy');
      this.updateActions();
    }
  }

  async refreshCart(data, button) {
    const cartComponent = document.querySelector('cart-component');

    if (cartComponent && typeof cartComponent.refresh === 'function') {
      await cartComponent.refresh();
      if (typeof cartComponent.renderCartBadgeSection === 'function') {
        cartComponent.renderCartBadgeSection(data.sections);
      }
      if (window.theme.cart?.type === 'page' && typeof cartComponent.openCartPage === 'function') {
        cartComponent.openCartPage();
      } else if (typeof cartComponent.openDrawer === 'function') {
        cartComponent.openDrawer(button);
      }
      return;
    }

    if (data.sections?.['cart-badge']) {
      const html = new DOMParser().parseFromString(data.sections['cart-badge'], 'text/html');
      const badge = html.querySelector('[data-cart-badge]');
      if (badge) {
        document.querySelectorAll('[data-cart-badge]').forEach((currentBadge) => {
          currentBadge.replaceWith(badge.cloneNode(true));
        });
      }
    }

    if (window.theme.cart?.type === 'page') {
      window.location.assign(window.theme.routes.cart.get);
      return;
    }

    const cartDialog = document.querySelector('#cart-dialog');
    const dialogHost = cartDialog?.closest('theme-dialog');
    if (dialogHost && typeof dialogHost.openDialog === 'function') {
      dialogHost.lastTrigger = button;
      dialogHost.openDialog();
    }
  }

  checkoutBundle(event) {
    event.preventDefault();
    const contents = this.getBundleContents();
    if (contents.length === 0) return;

    const items = contents.map((item) => `${item.variant_id}:${item.quantity}`).join(',');
    this.clearBundleContents();
    window.location.href = `${this.dataset.cartUrl}/${items}`;
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
